import { useCallback, useSyncExternalStore } from 'react';
import { storageKey } from '@/lib/storage-key.js';

export type SfxName =
  | 'click'
  | 'tick'
  | 'spinStart'
  | 'win'
  | 'bigWin'
  | 'lose'
  | 'scratch'
  | 'flip'
  | 'coin'
  | 'reveal'
  | 'ballBounce'
  | 'diceShake'
  | 'diceRoll'
  | 'kenoTick'
  | 'tilePop'
  | 'mineExplode'
  | 'heartbeat'
  | 'ascendChime'
  | 'mineCascade';

function storageKeyName() {
  return storageKey('sound.muted');
}

interface SoundState {
  ctx: AudioContext | null;
  master: GainNode | null;
  noise: AudioBuffer | null;
  muted: boolean;
}

const state: SoundState = {
  ctx: null,
  master: null,
  noise: null,
  muted: readMutedFromStorage(),
};

const listeners = new Set<() => void>();

function readMutedFromStorage(): boolean {
  try {
    return localStorage.getItem(storageKeyName()) === 'true';
  } catch {
    return false;
  }
}

function notify(): void {
  for (const l of listeners) l();
}

function ensureContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    if (!state.ctx) {
      const Ctor: typeof AudioContext | undefined =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!Ctor) return null;
      state.ctx = new Ctor();
      state.master = state.ctx.createGain();
      state.master.gain.value = 0.7;
      state.master.connect(state.ctx.destination);
    }
    if (state.ctx.state === 'suspended') {
      void state.ctx.resume();
    }
    return state.ctx;
  } catch {
    return null;
  }
}

function noiseBuffer(ctx: AudioContext): AudioBuffer {
  if (state.noise) return state.noise;
  const length = Math.floor(ctx.sampleRate * 0.5);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  state.noise = buffer;
  return buffer;
}

interface ToneOptions {
  freq: number;
  start: number;
  duration: number;
  type?: OscillatorType;
  gain?: number;
  endFreq?: number;
  destination?: AudioNode;
}

function tone(ctx: AudioContext, opts: ToneOptions): void {
  const out = opts.destination ?? state.master;
  if (!out) return;
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = opts.type ?? 'triangle';
  osc.frequency.setValueAtTime(opts.freq, opts.start);
  if (opts.endFreq != null) {
    osc.frequency.exponentialRampToValueAtTime(
      Math.max(1, opts.endFreq),
      opts.start + opts.duration,
    );
  }
  const peak = opts.gain ?? 0.2;
  env.gain.setValueAtTime(0.0001, opts.start);
  env.gain.exponentialRampToValueAtTime(peak, opts.start + 0.012);
  env.gain.exponentialRampToValueAtTime(0.0001, opts.start + opts.duration);
  osc.connect(env);
  env.connect(out);
  osc.start(opts.start);
  osc.stop(opts.start + opts.duration + 0.02);
}

function noiseBurst(
  ctx: AudioContext,
  start: number,
  duration: number,
  filterType: BiquadFilterType,
  freq: number,
  gain: number,
  freqEnd?: number,
  q = 1.2,
): void {
  if (!state.master) return;
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx);
  const filter = ctx.createBiquadFilter();
  filter.type = filterType;
  filter.frequency.setValueAtTime(freq, start);
  if (freqEnd != null) {
    filter.frequency.linearRampToValueAtTime(freqEnd, start + duration);
  }
  filter.Q.value = q;
  const env = ctx.createGain();
  env.gain.setValueAtTime(0.0001, start);
  env.gain.exponentialRampToValueAtTime(gain, start + 0.015);
  env.gain.exponentialRampToValueAtTime(0.0001, start + duration);
  src.connect(filter);
  filter.connect(env);
  env.connect(state.master);
  src.start(start);
  src.stop(start + duration + 0.02);
}

// A short, grittier scrape: a bandpass-swept white-noise body with a slightly
// resonant top layer so rapid calls overlap into a continuous scratch. Pleasant,
// not harsh — capped Q and modest gain.
function scratchScrape(ctx: AudioContext, start: number): void {
  // randomise length/sweep a touch so repeated scrapes don't sound looped
  const dur = 0.18 + Math.random() * 0.08; // ~180-260ms
  const lo = 900 + Math.random() * 300;
  const hi = 2600 + Math.random() * 600;
  // main scrape body: bandpass sweep up
  noiseBurst(ctx, start, dur, 'bandpass', lo, 0.075, hi, 3.5);
  // gritty top layer: a tighter, quieter resonant sweep the other direction
  noiseBurst(ctx, start, dur * 0.85, 'bandpass', hi, 0.03, lo + 400, 6);
}

// note frequencies (equal temperament, A4 = 440)
const C5 = 523.25;
const D5 = 587.33;
const E5 = 659.25;
const F5 = 698.46;
const G5 = 783.99;
const A5 = 880.0;
const C6 = 1046.5;

function render(name: SfxName, ctx: AudioContext, pitchIndex = 0): void {
  const now = ctx.currentTime;
  switch (name) {
    case 'click':
      tone(ctx, { freq: 520, start: now, duration: 0.08, type: 'triangle', gain: 0.16 });
      break;
    case 'tick':
      tone(ctx, { freq: 1200, start: now, duration: 0.025, type: 'square', gain: 0.05 });
      break;
    case 'spinStart':
      tone(ctx, {
        freq: 220,
        endFreq: 880,
        start: now,
        duration: 0.28,
        type: 'sawtooth',
        gain: 0.14,
      });
      noiseBurst(ctx, now, 0.28, 'bandpass', 600, 0.06, 2400);
      break;
    case 'win': {
      const seq = [C5, E5, G5];
      seq.forEach((f, i) => {
        tone(ctx, {
          freq: f,
          start: now + i * 0.1,
          duration: 0.4,
          type: 'triangle',
          gain: 0.18,
        });
        // shimmer harmonic for a soft reverb-ish tail
        tone(ctx, {
          freq: f * 2,
          start: now + i * 0.1,
          duration: 0.5,
          type: 'sine',
          gain: 0.05,
        });
      });
      break;
    }
    case 'bigWin': {
      const seq = [C5, E5, G5, C6, G5];
      seq.forEach((f, i) => {
        tone(ctx, {
          freq: f,
          start: now + i * 0.11,
          duration: 0.5,
          type: 'triangle',
          gain: 0.2,
        });
        tone(ctx, {
          freq: f * 1.5,
          start: now + i * 0.11,
          duration: 0.55,
          type: 'sine',
          gain: 0.07,
        });
      });
      tone(ctx, { freq: C6, start: now + 0.55, duration: 0.7, type: 'sine', gain: 0.12 });
      break;
    }
    case 'lose':
      tone(ctx, { freq: F5, start: now, duration: 0.18, type: 'sine', gain: 0.12 });
      tone(ctx, { freq: D5, start: now + 0.14, duration: 0.3, type: 'sine', gain: 0.12 });
      break;
    case 'scratch':
      scratchScrape(ctx, now);
      break;
    case 'flip':
      noiseBurst(ctx, now, 0.14, 'bandpass', 400, 0.08, 3000);
      break;
    case 'coin':
      tone(ctx, { freq: A5, start: now, duration: 0.06, type: 'square', gain: 0.12 });
      tone(ctx, { freq: C6 * 1.2, start: now + 0.05, duration: 0.12, type: 'square', gain: 0.12 });
      break;
    case 'reveal':
      tone(ctx, { freq: G5, endFreq: C6, start: now, duration: 0.45, type: 'sine', gain: 0.08 });
      tone(ctx, { freq: C6, endFreq: C6 * 1.5, start: now + 0.06, duration: 0.4, type: 'sine', gain: 0.05 });
      break;
    case 'ballBounce':
      // short woody tap; pan handled by caller via destination if needed
      tone(ctx, { freq: 320 + Math.random() * 80, start: now, duration: 0.05, type: 'triangle', gain: 0.08 });
      break;
    case 'diceShake': {
      // Dice knocking together inside the bowl — a sharp clack + rattly noise burst,
      // pitch-jittered per call so the throttled repeats read as a real rattle.
      const j = 0.8 + Math.random() * 0.5;
      noiseBurst(ctx, now, 0.08, 'bandpass', 2000 * j, 0.13, 3400, 4);
      tone(ctx, { freq: 1050 * j, start: now, duration: 0.03, type: 'triangle', gain: 0.14 });
      tone(ctx, { freq: 520 * j, start: now + 0.012, duration: 0.04, type: 'triangle', gain: 0.09 });
      break;
    }
    case 'diceRoll':
      noiseBurst(ctx, now, 0.18, 'bandpass', 600, 0.07, 1600, 2);
      tone(ctx, { freq: 240, start: now, duration: 0.16, type: 'triangle', gain: 0.06 });
      break;
    case 'kenoTick':
      tone(ctx, { freq: 900 + pitchIndex * 60, start: now, duration: 0.04, type: 'square', gain: 0.06 });
      break;
    case 'tilePop': {
      // Rising-pitch gem pop — pitch escalates with each safe reveal (pitchIndex = revealedTiles.length)
      // Base note C5, step up in semitones (factor ~1.059) per reveal, capped at 24 semitones (~C7).
      const semitones = Math.min(pitchIndex, 24);
      const freq = C5 * Math.pow(2, semitones / 12);
      tone(ctx, { freq, start: now, duration: 0.12, type: 'triangle', gain: 0.18 });
      // bright harmonic shimmer
      tone(ctx, { freq: freq * 2, start: now + 0.015, duration: 0.09, type: 'sine', gain: 0.06 });
      noiseBurst(ctx, now, 0.06, 'highpass', 3000, 0.03);
      break;
    }
    case 'mineExplode':
      // Low boom + filtered noise burst simulating an explosion
      tone(ctx, { freq: 60, endFreq: 30, start: now, duration: 0.45, type: 'sawtooth', gain: 0.25 });
      tone(ctx, { freq: 120, endFreq: 40, start: now, duration: 0.3, type: 'sawtooth', gain: 0.15 });
      noiseBurst(ctx, now, 0.5, 'lowpass', 800, 0.3, 100, 0.8);
      noiseBurst(ctx, now + 0.04, 0.25, 'bandpass', 2000, 0.12, 400, 1.5);
      break;
    case 'heartbeat': {
      // A two-thump heartbeat that tightens (faster gap, higher pitch, louder)
      // as tension climbs. pitchIndex = revealedTiles.length.
      const tension = Math.min(pitchIndex, 16) / 16; // 0 → 1
      const baseFreq = 80 + tension * 40; // 80 → 120 Hz
      const gap = 0.14 - tension * 0.05; // gap tightens with tension
      const gain = 0.16 + tension * 0.1;
      tone(ctx, { freq: baseFreq, endFreq: baseFreq * 0.6, start: now, duration: 0.1, type: 'sine', gain });
      tone(ctx, { freq: baseFreq * 0.95, endFreq: baseFreq * 0.55, start: now + gap, duration: 0.12, type: 'sine', gain: gain * 0.85 });
      // sub-thud body so it's felt, not just heard
      tone(ctx, { freq: 45, start: now, duration: 0.08, type: 'triangle', gain: 0.1 * (0.6 + tension) });
      break;
    }
    case 'ascendChime': {
      // Ascending arpeggio for cashout — longer + brighter as the multiplier tier climbs.
      // pitchIndex = intensity tier (0..3).
      const tier = Math.min(pitchIndex, 3);
      const ladder = [C5, E5, G5, C6, D5 * 2, E5 * 2, G5 * 2];
      const notes = ladder.slice(0, 4 + tier);
      notes.forEach((f, i) => {
        tone(ctx, { freq: f, start: now + i * 0.07, duration: 0.32, type: 'triangle', gain: 0.17 });
        tone(ctx, { freq: f * 2, start: now + i * 0.07, duration: 0.26, type: 'sine', gain: 0.045 });
      });
      // sparkle tail scaling with tier
      noiseBurst(ctx, now + notes.length * 0.07, 0.18 + tier * 0.05, 'highpass', 4000, 0.03 + tier * 0.01);
      break;
    }
    case 'mineCascade': {
      // Ominous tick for each staggered mine reveal; pitch descends per index for dread.
      const freq = Math.max(140, 320 - pitchIndex * 20);
      tone(ctx, { freq, endFreq: freq * 0.7, start: now, duration: 0.09, type: 'sawtooth', gain: 0.1 });
      noiseBurst(ctx, now, 0.07, 'lowpass', 1200, 0.06, 300, 0.9);
      break;
    }
  }
}

export function playSfx(name: SfxName, pitchIndex = 0): void {
  if (state.muted) return;
  try {
    const ctx = ensureContext();
    if (!ctx) return;
    render(name, ctx, pitchIndex);
  } catch {
    // audio must never break gameplay
  }
}

export function isMuted(): boolean {
  return state.muted;
}

export function setMuted(m: boolean): void {
  state.muted = m;
  try {
    localStorage.setItem(storageKeyName(), m ? 'true' : 'false');
  } catch {
    // ignore persistence failures
  }
  notify();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): boolean {
  return state.muted;
}

export function useGameSound(): {
  play: (n: SfxName) => void;
  muted: boolean;
  toggleMute: () => void;
} {
  const muted = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const play = useCallback((n: SfxName) => playSfx(n), []);
  const toggleMute = useCallback(() => setMuted(!state.muted), []);
  return { play, muted, toggleMute };
}
